<script setup lang="ts">
import { computed, ref } from 'vue'
import { useStore } from 'vuex'
import { useForm } from 'vee-validate'
import { toTypedSchema } from '@vee-validate/yup'
import { codeSchema } from '@/types'
import FieldError from '@/components/views/manage-account/FieldError.vue'

const store = useStore()

const validationSchema = toTypedSchema(codeSchema)
const { handleSubmit, errors, defineField } = useForm({
  validationSchema,
})
const [code] = defineField('code')

const buttonLoadingState = ref(false)

const email = computed(() => store.state.auth.authEmail)
const submitLabel = computed(() =>
  store.state.auth.authIntent === 'signup'
    ? 'ManageAccount.Login.CodeForm.Button.ContinueText'
    : 'ManageAccount.Login.CodeForm.Button.Text'
)

const loginWithCode = handleSubmit(async (values) => {
  buttonLoadingState.value = true

  try {
    await store.dispatch('auth0PasswordlessLogin', {
      email: email.value,
      verificationCode: values.code,
    })
  } catch (error) {
    buttonLoadingState.value = false
  }

  buttonLoadingState.value = false
})
</script>

<template>
  <form class="sa-auth-form" novalidate @submit.prevent="loginWithCode">
    <div
      class="sa-form-group"
      :class="{ 'sa-form-group--invalid': errors.code?.length }"
    >
      <label for="code" class="sa-form-label">
        {{ $t('ManageAccount.Login.CodeForm.FieldLabel') }}
      </label>

      <input
        id="code"
        v-model="code"
        spellcheck="false"
        type="text"
        inputmode="numeric"
        class="sa-input sa-input--code"
        :class="{ 'is-invalid': errors.code?.length }"
        name="code"
        minlength="6"
        maxlength="6"
        autocomplete="one-time-code"
        :aria-invalid="errors.code?.length ? true : undefined"
        :aria-describedby="errors.code?.length ? 'code-error' : undefined"
        :placeholder="$t('ManageAccount.Login.CodeForm.FieldPlaceholder')"
      />

      <FieldError
        :error="errors.code"
        error-id="code-error"
        class="sa-auth-form__error"
      />
    </div>

    <button
      type="submit"
      class="sa-btn-primary sa-auth-form__submit"
      :disabled="buttonLoadingState"
      :aria-busy="buttonLoadingState"
    >
      <span
        v-if="buttonLoadingState"
        class="spinner sa-btn-spinner"
        aria-hidden="true"
      />
      <span>{{ $t(submitLabel) }}</span>
    </button>
  </form>
</template>
